import { useTranslation } from 'react-i18next'
import { JsonLd } from 'react-schemaorg'
import type {
  FAQPage,
  HowTo,
  Organization,
  SearchAction,
  WebSite,
} from 'schema-dts'
import { DefaultMetaTitle } from 'src/components/MetaTags'
import { useDailyQuestsDialog } from 'src/components/Quest/QuestDialog'
import { ORIGIN } from 'src/utils/constants'
import { type ArtworkFeedResult } from '~/components/ArtworkList/useArtworkFeed'
import ArtworkListPage from './ArtworkListPage'

type SearchActionWithQueryInput = SearchAction & {
  'query-input': string
}

function HomePageJsonLD() {
  const { t } = useTranslation(['common'])

  return (
    <>
      <JsonLd<Organization>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'PixAI',
          description:
            "PixAI's feature-rich AI art generator for high-quality anime AI art. Experience a wide range of AI tools and anime character generation templates at the comfort of your browser.",
          url: ORIGIN,
          logo: 'https://imagedelivery.net/5ejkUOtsMH5sf63fw6q33Q/9b74fa7b-a333-49cb-e82e-657f70410200/public',
          sameAs: [
            'https://twitter.com/PixAI_Official',
            'https://www.facebook.com/profile.php?id=100081244069311',
            'https://www.instagram.com/pixai_official',
            'https://www.tiktok.com/@pixai.art',
          ],
        }}
      />
      <JsonLd<WebSite>
        item={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'PixAI',
          url: ORIGIN,
          description:
            "PixAI's feature-rich AI art generator for high-quality anime AI art. Experience a wide range of AI tools and anime character generation templates at the comfort of your browser.",
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${ORIGIN}/search?q={search_term}`,
            },
            'query-input': 'required name=search_term',
          } as SearchActionWithQueryInput,
        }}
      />
      <JsonLd<HowTo>
        item={{
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: t('common:meta.json-ld.howto.title') ?? '',
          step: Array.from({ length: 6 }, (_, i) => i + 1).map(step => ({
            '@type': 'HowToStep',
            text: t(`common:meta.json-ld.howto.step-${step}`) ?? '',
          })),
        }}
      />
      <JsonLd<FAQPage>
        item={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          name: t('common:meta.json-ld.howto.title') ?? '',
          mainEntity: Array.from({ length: 2 }, (_, i) => i + 1).map(step => ({
            '@type': 'Question',
            name: t(`common:meta.json-ld.faq.q-${step}`) ?? '',
            acceptedAnswer: {
              '@type': 'Answer',
              text: t(`common:meta.json-ld.faq.a-${step}`) ?? '',
            },
          })),
        }}
      />
    </>
  )
}

const HomePageWrapper = (props: {
  ssrInitialConnectionData?: Parameters<
    typeof ArtworkListPage
  >[0]['ssrInitialConnectionData']
}) => {
  useDailyQuestsDialog({ trackingProps: { location: 'home' } })

  return (
    <>
      <DefaultMetaTitle />
      <HomePageJsonLD />
      <ArtworkListPage
        ssrInitialConnectionData={props.ssrInitialConnectionData}
      />
    </>
  )
}

export const Component = HomePageWrapper
